import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { ObjectHelper } from '@barthauer/abc/core';
import { ObjectSpaceService, ObjectTypeInfoViewModel } from '@barthauer/abc/core/data';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { forkJoin, Observable, of } from 'rxjs';
import { concatMap, filter, flatMap, groupBy, map, toArray } from 'rxjs/operators';
import { ContextMenuController } from '../../context-menu-controller';
import { ChoiceMenuCategory, ChoiceMenuItem } from '../../models/choicemenu.model';
import { ContextMenuButton } from '../../models/contextmenu.model';
import { DataEntity, DataEntityInfo } from '../../models/element.model';
import { MapLayer } from '../../models/map.model';
import { MapService } from '../../services/map.service';

declare var ol: any;

@Component({
    selector: 'abc-graphics-choicemenu',
    templateUrl: './choicemenu.component.html',
    styleUrls: ['./choicemenu.component.scss'],
    standalone: false
})
export class ChoicemenuComponent implements OnInit {
  @ViewChild('featureList', { static: true }) featureList: ElementRef;

  public controller: ContextMenuController;
  @Output() itemAction = new EventEmitter();

  public selectedObjects: ChoiceMenuCategory[] = null;
  public olmap: any;
  public popup: any;
  public lastCoordinate: any;
  public commandSelected: boolean;
  public res = ResourceSet.Empty;

  constructor(
    private oss: ObjectSpaceService,
    private mapService: MapService,
    private resourceService: ResourceManagerService
  ) {}

  async ngOnInit() {
    this.res = await this.resourceService.getResourceSet('abc-graphics-adapters-open-layers');
  }

  initMapOverlay(olmap: any) {
    this.olmap = olmap;
    this.popup = new ol.Overlay({
      element: this.featureList.nativeElement,
      autoPan: true,
      autoPanAnimation: {
        duration: 250,
      },
    });

    this.olmap.addOverlay(this.popup);
  }

  closePopup(): boolean {
    event.stopPropagation();
    if (this.popup) {
      if (!this.commandSelected && !this.selectedObjects?.length) {
        this.mapService.navigateToObjects([]);
      }
      this.commandSelected = false;
      this.popup.setPosition(undefined);
    }

    return false;
  }

  refreshPopup() {
    if (this.popup) this.popup.changed();
  }

  fill(clickedDataEntities: DataEntity[], clickedLayerEntities: DataEntity[]): Observable<ChoiceMenuCategory[]> {
    let categories: Observable<ChoiceMenuCategory[]> = of([]);
    if (clickedDataEntities?.length) {
      categories = categories.pipe(
        concatMap((arr) =>
          this.mapService.getDataEntityInfos(clickedDataEntities).pipe(
            concatMap((_) => {
              if (!_) {
                this.mapService.sendMapShortMessage(this.res.get('Controls-ChoiceMenu-NoDataError'));
                return of(arr);
              }
              return forkJoin(
                _.map((info) => {
                  return this.oss.getObjectType(info.entity.objectType).pipe(
                    concatMap((ot) => {
                      const e = clickedDataEntities.find((_) => _.id === info.entity.id) ?? info.entity;
                      return this.getObjectTypeButtonsFromConfiguration(ot, e.feature?.layerdefinition).pipe(
                        map((buttons) => {
                          if (buttons.length) {
                            return {
                              name: info.category,
                              items: [
                                {
                                  name: info.displayText,
                                  entity: new DataEntityInfo(
                                    e,
                                    info.category,
                                    info.displayText,
                                    info.commandsBadgeValue,
                                    info.commandsEnabled
                                  ),
                                  buttons: buttons,
                                },
                              ],
                            } as ChoiceMenuCategory;
                          }
                        })
                      );
                    })
                  );
                })
              );
            })
          )
        )
      );
    }
    if (clickedLayerEntities?.length) {
      categories = categories.pipe(
        map((arr) => {
          for (const f of clickedLayerEntities) {
            const buttons = this.controller.getLayerContextMenuButtonsFromConfiguration(f.feature?.layerdefinition);
            if (buttons?.length) {
              const pos = f.objectType.toString().lastIndexOf('\\');
              let categoryName = f.objectType.toString().substring(pos + 1);
              let cat = arr.find((_) => _.name === categoryName);
              if (!cat) {
                cat = {
                  name: categoryName,
                  items: [],
                } as ChoiceMenuCategory;
                arr.push(cat);
              }
              cat.items.push({
                name: f.id,
                entity: new DataEntityInfo(f, categoryName, f.id, null, null),
                buttons: buttons,
              });
            }
          }
          return arr;
        })
      );
    }

    let groupedCategories = categories.pipe(
      concatMap((_) => _),
      filter((_) => ObjectHelper.isObject(_)),
      groupBy(
        (_) => _.name,
        (_) => _.items
      ),
      flatMap((grp) => {
        return grp.pipe(
          toArray(),
          map((_) => {
            return {
              name: grp.key,
              items: this.getItems(_),
            } as ChoiceMenuCategory;
          })
        );
      }),
      toArray()
    );
    return groupedCategories;
  }

  private getItems(items: ChoiceMenuItem[][]): ChoiceMenuItem[] {
    let result: ChoiceMenuItem[] = [];
    for (let arr of items) {
      result.push(...arr);
    }
    return result;
  }

  getObjectTypeButtonsFromConfiguration(
    ot: ObjectTypeInfoViewModel,
    layerdefinition: MapLayer
  ): Observable<ContextMenuButton[]> {
    return this.controller.getContextMenuButtonsFromConfiguration(ot.name, layerdefinition).pipe(
      map((buttons) => {
        var ctxButtons = [];
        for (let b of buttons) {
          if (b.enabled || b.enabledCondition || b.enabledByBadgeValue) {
            const newbutton = new ContextMenuButton();
            Object.assign(newbutton, b);
            if (newbutton.enabledCondition || newbutton.enabledByBadgeValue) {
              newbutton.enabled = false;
            }
            if (newbutton.badgeValueExpression) {
              newbutton.badgeHidden = true;
            }
            ctxButtons.push(newbutton);
          }
        }
        return ctxButtons;
      })
    );
  }

  onAction(entity: DataEntityInfo, btn: ContextMenuButton, event: any) {
    this.commandSelected = true;
    this.mapService.navigateToObjects([entity.entity]);
    btn.action(entity.entity);
    this.closePopup();
  }

  setVisible(visible: boolean): void {
    if (visible) {
      this.commandSelected = false;
    }
    if (this.featureList != undefined) {
      this.featureList.nativeElement.style.visibility = visible ? 'visible' : 'hidden';
      const popupElement = this.featureList.nativeElement.parentElement?.parentElement;
      if (popupElement) popupElement.style.zIndex = 99999;
    }
  }

  public isVisible(): boolean {
    return this.popup?.getPosition() && this.featureList?.nativeElement?.style?.visibility == 'visible';
  }
}
